import { supabase } from '../cloud/supabaseClient';
import { db } from '../../db/database';
import type { AddOnRecord, OutboxRecord, ServiceRecord } from '../../db/types';
import type { SyncSnapshot, SyncStatus } from './syncTypes';

const RETRY_DELAYS_MS = [5000, 15000, 30000, 60000, 300000] as const;
const EVENT_NAME = 'babs:sync-changed';
const OUTBOX_EVENT_NAME = 'babs:outbox-changed';
class SyncConflictError extends Error {}
let running = false;
let wakeTimer: ReturnType<typeof setTimeout> | undefined;
let localDataRecoveryOpen = false;

export function setLocalDataRecoveryOpen(isOpen: boolean) {
  localDataRecoveryOpen = isOpen;
}

async function getSyncMetadata(key: string) {
  return db.syncMetadata.get(key);
}
async function putSyncMetadata(key: string, value: string) {
  await db.syncMetadata.put({ key, value, updatedAt: new Date().toISOString() });
}
async function listPending() {
  return db.outbox.where('status').equals('PENDING').sortBy('createdAt');
}

function emitSyncChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

async function reportDeviceHealth(status: SyncStatus, pendingCount: number) {
  const device = await db.settings.get('cloud.deviceId');
  if (!device?.value) return;
  try {
    await supabase.rpc('record_device_sync_health', {
      p_device_id: device.value,
      p_sync_status: status,
      p_pending_transactions: pendingCount,
    });
  } catch {
    // Telemetry must never block POS transaction processing.
  }
}

async function writeStatus(status: SyncStatus, error?: string) {
  const now = new Date().toISOString();
  await putSyncMetadata('status', status);
  if (error) await putSyncMetadata('lastError', error);
  if (status === 'ONLINE') await putSyncMetadata('lastSyncAt', now);
  const pendingCount = await db.outbox
    .where('status')
    .anyOf('PENDING', 'SYNCING', 'FAILED')
    .count();
  await reportDeviceHealth(status, pendingCount);
  emitSyncChanged();
}

export async function getSyncSnapshot(): Promise<SyncSnapshot> {
  const [status, lastSyncAt, lastError, pendingCount] = await Promise.all([
    getSyncMetadata('status'),
    getSyncMetadata('lastSyncAt'),
    getSyncMetadata('lastError'),
    db.outbox.where('status').anyOf('PENDING', 'SYNCING', 'FAILED').count(),
  ]);
  const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
  let resolved: SyncStatus = browserOnline ? 'ONLINE' : 'OFFLINE';
  if (status?.value === 'SYNCING') resolved = 'SYNCING';
  if (status?.value === 'SYNC_ERROR') resolved = 'SYNC_ERROR';
  if (!browserOnline) resolved = 'OFFLINE';
  return {
    status: resolved,
    pendingCount,
    lastSyncAt: lastSyncAt?.value,
    lastError: lastError?.value,
  };
}

async function currentCloudDeviceId(): Promise<string> {
  const device = await db.settings.get('cloud.deviceId');
  if (device?.value) return device.value;
  const { data, error } = await supabase
    .from('devices')
    .select('id')
    .eq('active', true)
    .order('created_at')
    .limit(1)
    .single();
  if (error || !data) throw new Error('No active cloud device is available for this POS.');
  await db.settings.put({
    key: 'cloud.deviceId',
    value: data.id,
    updatedAt: new Date().toISOString(),
  });
  return data.id;
}

async function processEvent(event: OutboxRecord): Promise<void> {
  const payload = JSON.parse(event.payload) as Record<string, unknown>;
  const needsDevice =
    event.eventType === 'ORDER_CREATED' ||
    event.eventType === 'ORDER_CLAIMED' ||
    event.eventType === 'ORDER_VOIDED' ||
    event.eventType === 'SHIFT_OPENED' ||
    event.eventType === 'SHIFT_CLOSED';
  const deviceId = needsDevice ? await currentCloudDeviceId() : undefined;
  if (event.eventType === 'ORDER_CREATED') {
    const order = { ...(payload['order'] as Record<string, unknown>), deviceId };
    payload['order'] = order;
  }
  if (event.eventType === 'ORDER_CLAIMED' || event.eventType === 'ORDER_VOIDED')
    payload['deviceId'] = deviceId;
  if (event.eventType === 'SHIFT_OPENED' || event.eventType === 'SHIFT_CLOSED') {
    payload['shift'] = { ...(payload['shift'] as Record<string, unknown>), deviceId };
    const { data, error } = await supabase.rpc('process_shift_sync_event', {
      p_event_id: event.id,
      p_event_type: event.eventType,
      p_idempotency_key: event.idempotencyKey,
      p_aggregate_id: event.aggregateId,
      p_payload: payload,
    });
    if (error) throw error;
    assertAcknowledgement(data, event);
    return;
  }

  const { data, error } = await supabase.rpc('process_sync_event', {
    p_event_id: event.id,
    p_event_type: event.eventType,
    p_idempotency_key: event.idempotencyKey,
    p_aggregate_id: event.aggregateId,
    p_payload: payload,
  });
  if (error) throw error;
  assertAcknowledgement(data, event);
}

type SyncAcknowledgement = {
  status?: unknown;
  idempotency_key?: unknown;
  response?: unknown;
  order_id?: unknown;
  customer_id?: unknown;
  shift_id?: unknown;
  entity?: unknown;
  entity_id?: unknown;
};

function acknowledgementId(value: SyncAcknowledgement, event: OutboxRecord) {
  const field = {
    ORDER: 'order_id',
    CUSTOMER: 'customer_id',
    SHIFT: 'shift_id',
  }[event.aggregateType];
  return value[field as keyof SyncAcknowledgement];
}

function assertAcknowledgement(data: unknown, event: OutboxRecord) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Sync acknowledgement was missing or invalid.');
  }

  const acknowledgement = data as SyncAcknowledgement;
  const status = acknowledgement.status;
  if (status === 'CONFLICT') {
    throw new SyncConflictError('The server reported a sync conflict.');
  }
  if (status !== 'PROCESSED' && status !== 'ALREADY_PROCESSED') {
    throw new Error('The server returned an unrecognized sync acknowledgement.');
  }

  if (
    acknowledgement.idempotency_key !== undefined &&
    acknowledgement.idempotency_key !== event.idempotencyKey
  ) {
    throw new Error('The server acknowledgement did not match this event.');
  }

  const nested =
    acknowledgement.response && typeof acknowledgement.response === 'object'
      ? (acknowledgement.response as SyncAcknowledgement)
      : null;
  if (
    nested?.status !== undefined &&
    nested.status !== 'PROCESSED' &&
    nested.status !== 'ALREADY_PROCESSED'
  ) {
    throw new Error('The server acknowledgement did not confirm processing.');
  }

  const aggregateId =
    acknowledgementId(acknowledgement, event) ??
    (nested ? acknowledgementId(nested, event) : undefined);
  if (aggregateId !== event.aggregateId) {
    throw new Error('The server acknowledgement did not match the submitted aggregate.');
  }
}

async function markSynced(event: OutboxRecord) {
  await db.outbox.update(event.id, { status: 'SYNCED', updatedAt: new Date().toISOString() });
}

async function markFailure(event: OutboxRecord, error: unknown) {
  const isConflict = error instanceof SyncConflictError;
  const attempts = isConflict ? RETRY_DELAYS_MS.length + 1 : event.attempts + 1;
  const message = error instanceof Error ? error.message : String(error);
  await db.outbox.update(event.id, {
    status: 'FAILED',
    attempts,
    updatedAt: new Date().toISOString(),
  });
  await putSyncMetadata('lastError', message);
  if (!isConflict && attempts <= RETRY_DELAYS_MS.length) {
    clearTimeout(wakeTimer);
    wakeTimer = setTimeout(() => void processPending(), RETRY_DELAYS_MS[attempts - 1]);
  }
}

async function normalizeInterruptedAndFailedToPending() {
  // A terminated PWA can leave an event in SYNCING. Reuse its original IDs and
  // idempotency key so a server commit followed by a lost response is safe to retry.
  const interrupted = await db.outbox.where('status').equals('SYNCING').toArray();
  for (const event of interrupted) {
    await db.outbox.update(event.id, { status: 'PENDING', updatedAt: new Date().toISOString() });
  }
  const failed = await db.outbox.where('status').equals('FAILED').toArray();
  for (const event of failed) {
    if (event.attempts <= RETRY_DELAYS_MS.length)
      await db.outbox.update(event.id, { status: 'PENDING', updatedAt: new Date().toISOString() });
  }
}

export async function processPending(): Promise<void> {
  if (localDataRecoveryOpen || running) return;
  running = true;
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const session = sessionData.session;
    if (
      localDataRecoveryOpen ||
      sessionError ||
      !session?.user?.id ||
      !session.access_token ||
      (session.expires_at !== undefined && session.expires_at <= Math.floor(Date.now() / 1000))
    ) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await writeStatus('OFFLINE');
      return;
    }

    const { data: userData, error: userError } = await supabase.auth.getUser(session.access_token);
    if (localDataRecoveryOpen || userError || userData.user?.id !== session.user.id) return;

    await normalizeInterruptedAndFailedToPending();
    const pending = await listPending();
    if (pending.length === 0) {
      void refreshCatalogIfAuthenticated();
      await writeStatus('ONLINE');
      return;
    }
    await writeStatus('SYNCING');
    for (const event of pending) {
      if (localDataRecoveryOpen) return;
      await db.outbox.update(event.id, { status: 'SYNCING', updatedAt: new Date().toISOString() });
      emitSyncChanged();
      try {
        await processEvent(event);
        await markSynced(event);
      } catch (error) {
        await markFailure(event, error);
        await writeStatus('SYNC_ERROR', error instanceof Error ? error.message : String(error));
        continue;
      }
    }
    const remaining = await listPending();
    const failed = await db.outbox.where('status').equals('FAILED').count();
    if (remaining.length === 0 && failed === 0) {
      void refreshCatalogIfAuthenticated();
      await writeStatus('ONLINE');
    } else await writeStatus('SYNC_ERROR');
  } finally {
    running = false;
    emitSyncChanged();
  }
}

async function refreshCatalogIfAuthenticated() {
  if (localDataRecoveryOpen || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
  const { data } = await supabase.auth.getSession();
  if (!data.session) return;
  try {
    await refreshServerAuthoritativeCatalog();
  } catch {
    /* catalog refresh must never block transaction sync */
  }
}

export function requestSync() {
  if (localDataRecoveryOpen) return;
  void processPending();
}

export function subscribeSyncEngine() {
  if (typeof window === 'undefined') return () => undefined;
  const onOnline = () => requestSync();
  const onVisible = () => {
    if (document.visibilityState === 'visible') requestSync();
  };
  const onChanged = () => requestSync();
  const catalogRefresh = window.setInterval(() => requestSync(), 60_000);
  window.addEventListener('online', onOnline);
  window.addEventListener('visibilitychange', onVisible);
  window.addEventListener(OUTBOX_EVENT_NAME, onChanged);
  requestSync();
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener(OUTBOX_EVENT_NAME, onChanged);
    window.clearInterval(catalogRefresh);
  };
}

export function notifyOutboxChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(OUTBOX_EVENT_NAME));
  emitSyncChanged();
}

export async function refreshServerAuthoritativeCatalog(): Promise<void> {
  if (localDataRecoveryOpen) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  const { data, error } = await supabase.rpc('sync_server_catalog');
  if (error) throw error;
  type CloudService = {
    id: string;
    name: string;
    description: string | null;
    category: string;
    pricing_type: ServiceRecord['pricingType'];
    price_centavos: number;
    minimum_kg: number | null;
    load_capacity_kg: number | null;
    requires_weight: boolean;
    requires_item_selection: boolean;
    allows_quantity: boolean;
    allows_addons: boolean;
    active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
  };
  type CloudServiceItem = {
    id: string;
    service_id: string;
    name: string;
    unit_price_centavos: number;
    active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
  };
  type CloudAddon = {
    id: string;
    name: string;
    category: string;
    pricing_type: AddOnRecord['pricingType'];
    price_centavos: number;
    active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
  };
  const result = data as {
    services: CloudService[];
    service_items: CloudServiceItem[];
    addons: CloudAddon[];
  };
  if (
    !result ||
    !Array.isArray(result.services) ||
    !Array.isArray(result.addons) ||
    result.services.filter((row) => row.active).length === 0 ||
    result.addons.filter((row) => row.active).length === 0
  ) {
    throw new Error('Catalog response was empty; the saved local catalog was kept.');
  }
  await db.transaction('rw', [db.services, db.serviceItems, db.addons], async () => {
    await db.services.clear();
    await db.serviceItems.clear();
    await db.addons.clear();
    await db.services.bulkPut(
      (result.services ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        category: row.category,
        description: row.description ?? undefined,
        pricingType: row.pricing_type,
        priceCentavos: Number(row.price_centavos),
        minimumKg: row.minimum_kg == null ? undefined : Number(row.minimum_kg),
        loadCapacityKg: row.load_capacity_kg == null ? undefined : Number(row.load_capacity_kg),
        requiresWeight: row.requires_weight,
        requiresItemSelection: row.requires_item_selection,
        allowsQuantity: row.allows_quantity,
        allowsAddons: row.allows_addons,
        active: row.active,
        sortOrder: row.sort_order,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })),
    );
    await db.serviceItems.bulkPut(
      (result.service_items ?? []).map((row) => ({
        id: row.id,
        serviceId: row.service_id,
        name: row.name,
        unitPriceCentavos: Number(row.unit_price_centavos),
        active: row.active,
        sortOrder: row.sort_order,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })),
    );
    await db.addons.bulkPut(
      (result.addons ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        category: row.category,
        pricingType: row.pricing_type,
        priceCentavos: Number(row.price_centavos),
        active: row.active,
        sortOrder: row.sort_order,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })),
    );
  });
  emitSyncChanged();
}

export const syncRetryDelaysMs = RETRY_DELAYS_MS;
